import { Link } from "react-router-dom";
import { ArrowRight, Heart, Users, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { IMAGES } from "@/config/siteConfig";
import { useQuote } from "@/context/QuoteContext";

const Segment = ({ id, image, overline, title, copy, points, cta, link, flip = false }) => {
  const { openQuote } = useQuote();
  return (
    <section className={`py-16 sm:py-24 ${flip ? "bg-white" : ""}`} data-testid={`${id}-section`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center`}>
          <div className={flip ? "lg:order-2" : ""}>
            <div className="overflow-hidden rounded-2xl shadow-lg">
              <img src={image} alt={title} loading="lazy" className="aspect-[4/3] w-full object-cover transition-transform duration-700 hover:scale-105" />
            </div>
          </div>
          <div className={flip ? "lg:order-1" : ""}>
            <p className="text-xs font-mono uppercase tracking-[0.2em] font-semibold text-[#C28519] mb-3 flex items-center gap-2">
              {id === "honeymoon" ? <Heart className="h-3.5 w-3.5" /> : <Users className="h-3.5 w-3.5" />} {overline}
            </p>
            <h2 className="font-serif text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-[#0F2F24] text-balance">{title}</h2>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">{copy}</p>
            <ul className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
              {points.map((p) => (
                <li key={p} className="flex items-start gap-2 text-sm text-[#1E2922]">
                  <Check className="mt-0.5 h-4 w-4 shrink-0 text-[#C28519]" /> {p}
                </li>
              ))}
            </ul>
            <div className="mt-8 flex flex-wrap gap-3">
              <Button onClick={() => openQuote()} data-testid={`${id}-cta-button`}
                className="bg-[#0F2F24] hover:bg-[#1a4232] text-white rounded-full px-6 h-11 font-semibold">
                {cta} <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <Button asChild variant="outline" className="rounded-full px-6 h-11 border-[#0F2F24]/30 text-[#0F2F24] hover:bg-[#E3EAE4]">
                <Link to={link} data-testid={`${id}-view-packages-link`}>View Packages</Link>
              </Button>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export const HoneymoonSection = () => (
  <Segment id="honeymoon" image={IMAGES.houseboat} overline="Honeymoon in Kashmir"
    title="Make Your Kashmir Honeymoon Unforgettable"
    copy="From peaceful moments on Dal Lake to snow-covered landscapes in Gulmarg and romantic stays surrounded by the mountains, Kashmir offers an unforgettable honeymoon experience."
    points={["Romantic hotels", "Private transfers", "Shikara experience", "Romantic experiences where available", "Gulmarg & Pahalgam", "Custom itineraries"]}
    cta="Plan My Honeymoon" link="/kashmir-honeymoon-packages" />
);

export const FamilySection = () => (
  <Segment id="family" flip image="/images/family-travellers.jpg" overline="Family Holidays"
    title="Kashmir Holidays for the Whole Family"
    copy="Create a comfortable Kashmir experience for the entire family with carefully selected hotels, private transportation and flexible sightseeing."
    points={["Family-friendly hotels", "Private vehicles", "Flexible itineraries", "Comfortable sightseeing", "Local assistance"]}
    cta="Plan My Family Holiday" link="/kashmir-family-packages" />
);
